import { useLocalSearchParams } from 'expo-router';
import { useMemo } from 'react';

/**
 * Read the `?asOf=<ms>` time-travel override from the current route. Pages
 * that have a chip-driven date picker write the override into the URL via
 * `router.replace(...?asOf=<ms>)`; every accounting page can then read the
 * same source of truth here, thread it into data hooks, and pass it forward
 * on outgoing navigation with `withAsOf`.
 *
 * `asOfQuery` is the trailing `?asOf=...` (or empty) — handy when you want
 * to forward it onto an external link (e.g. a print URL) without re-parsing.
 */
export function useAsOf(): {
	asOfMs: number | undefined;
	asOfQuery: string;
	withAsOf: (href: string) => string;
	} {
	const params = useLocalSearchParams<{ asOf?: string }>();
	const asOfMs = useMemo(() => {
		const n = Number(params.asOf);
		return Number.isFinite(n) && n > 0 ? n : undefined;
	}, [params.asOf]);

	return useMemo(() => {
		const asOfQuery = asOfMs ? `asOf=${asOfMs}` : '';
		const withAsOf = (href: string): string => {
			if (!asOfMs) return href;
			const sep = href.includes('?') ? '&' : '?';
			return `${href}${sep}${asOfQuery}`;
		};
		return { asOfMs, asOfQuery, withAsOf };
	}, [asOfMs]);
}
